// Task ID formats: MT#0001 (Main Task), ST#0001 (Sub Task)

export function isMainTask(taskId) {
  if (typeof taskId !== 'string') return false;
  return taskId.startsWith('MT#');
}

export function isSubTask(taskId) {
  if (typeof taskId !== 'string') return false;
  return taskId.startsWith('ST#');
}

export function getNumericId(taskId) {
  if (!taskId) return null;
  const match = String(taskId).match(/\d+/);
  return match ? parseInt(match[0], 10) : null;
}

// MT#0001 -> "1", ST#0042 -> "42"
export function getTaskDisplayId(taskId) {
  const num = getNumericId(taskId);
  return num !== null ? String(num) : taskId;
}

// Format a numeric ID as task ID string
// formatTaskId(1, 'main') -> 'MT#0001'
// formatTaskId(42, 'sub') -> 'ST#0042'
export function formatTaskId(numericId, type) {
  const prefix = type === 'main' ? 'MT#' : 'ST#';
  return `${prefix}${String(numericId).padStart(4, '0')}`;
}

// Get task type from ID
export function getTaskType(taskId) {
  if (isMainTask(taskId)) return 'maintask';
  if (isSubTask(taskId)) return 'subtask';
  return null;
}

// Normalise a numeric dependency reference to full subtask ID format.
// Always returns ST#NNNN format.
export function normaliseToSubtaskId(numericRef) {
  if (numericRef == null) return null;
  const str = String(numericRef);

  // Already in full ST# format - return as-is
  if (str.startsWith('ST#')) return str;

  // Must be numeric at this point
  if (!/^\d+$/.test(str)) return str;

  return `ST#${str.padStart(4, '0')}`;
}

// Look up blockers from a blockedTasksMap.
// Template mode uses composite keys (templateId:taskId) where templateId is:
//   - sourceAddinId for add-in tasks
//   - projectId for main template tasks
// Regular mode uses simple keys (taskId).
export function getBlockers(blockedTasksMap, taskId, projectId, isTemplateMode, sourceAddinId) {
  if (!blockedTasksMap || !taskId) return [];
  if (isTemplateMode) {
    const templateId = sourceAddinId ? sourceAddinId : projectId;
    if (!templateId) return [];
    const key = `${templateId}:${taskId}`;
    return blockedTasksMap.get(key) || [];
  }
  return blockedTasksMap.get(String(taskId)) || [];
}

// Parse a composite dependency key (tmpl_MAIN123:15) into its parts.
// Returns { templateId, numericRef } or null if invalid.
export function parseCompositeDependencyKey(key) {
  if (!key || typeof key !== 'string') return null;
  if (!key.includes(':')) return null;

  const colonIndex = key.lastIndexOf(':');
  const templateId = key.substring(0, colonIndex);
  const numericRef = key.substring(colonIndex + 1);

  if (!templateId.startsWith('tmpl_')) return null;
  const num = parseInt(numericRef, 10);
  if (isNaN(num) || num <= 0) return null;

  return { templateId, numericRef: num };
}

// Validate a composite dependency key format.
export function isValidCompositeDependencyKey(key) {
  return parseCompositeDependencyKey(key) !== null;
}

// Build a composite key for a task (used for blockedTasksMap keys).
// For add-in tasks, use sourceAddinId:taskId.
// For main template tasks, use projectId:taskId.
export function buildCompositeKey(task, mainTemplateId) {
  if (!task || !task.taskId) return null;
  const templateId = task.sourceAddinId ? task.sourceAddinId : mainTemplateId;
  if (!templateId) return null;
  return `${templateId}:${task.taskId}`;
}

// ==================================
// Global Ordering Functions
// ==================================

// Parse an order key (templateId:taskId) into its components.
// Order keys are used in combinedMainTaskOrder to identify tasks.
// Returns { templateId, taskId } or null if invalid.
export function parseOrderKey(key) {
  if (!key || typeof key !== 'string') return null;
  if (!key.includes(':')) return null;

  const colonIndex = key.indexOf(':');
  const templateId = key.substring(0, colonIndex);
  const taskId = key.substring(colonIndex + 1);

  if (!templateId.startsWith('tmpl_')) return null;
  if (!taskId) return null;

  return { templateId, taskId };
}

// Build an order key from task data.
// Uses _sourceTemplateId if available, otherwise projectId.
// Returns 'templateId:taskId' format.
export function buildOrderKey(task, mainTemplateId) {
  if (!task || !task.taskId) return null;
  const templateId = task._sourceTemplateId || task.sourceAddinId || mainTemplateId;
  if (!templateId) return null;
  return `${templateId}:${task.taskId}`;
}

// Filter order array to only include tasks from attached add-ins.
// attachedAddinIds should be an array of addinId values for attached add-ins.
// mainTemplateId is always considered "attached".
export function filterOrderByAttached(orderArray, mainTemplateId, attachedAddinIds) {
  if (!Array.isArray(orderArray)) return [];
  if (!mainTemplateId) return orderArray;

  const attachedSet = new Set(attachedAddinIds || []);
  attachedSet.add(mainTemplateId);

  return orderArray.filter(key => {
    const parsed = parseOrderKey(key);
    if (!parsed) return false;
    return attachedSet.has(parsed.templateId);
  });
}

// Apply stored order to an array of tasks.
// Returns new array with tasks sorted by stored order position.
// Tasks not in stored order are placed at the end with their original relative order.
export function applyStoredOrder(tasks, storedOrderMap, epicIdKey, mainTemplateId) {
  if (!Array.isArray(tasks)) return [];
  if (!storedOrderMap || typeof storedOrderMap !== 'object') return tasks;

  return tasks.map(task => {
    const taskKey = buildOrderKey(task, mainTemplateId);
    const epicId = task[epicIdKey];
    const epicOrder = storedOrderMap[epicId] || [];
    const storedIndex = epicOrder.indexOf(taskKey);

    if (storedIndex !== -1) {
      return { ...task, order: storedIndex };
    }
    return { ...task, order: (task.order || 0) + 1000 };
  });
}

